import { openDB } from 'idb';
import { DATA_VERSION } from './version';

// openDB never settles when the browser's IndexedDB is wedged, or when an
// upgrade is blocked by another open tab. Callers await it before every cached
// read, so an unbounded wait strands them forever instead of falling back to
// the network. Reject instead, and let the caller reach for the network.
export const DB_OPEN_TIMEOUT_MS = 5000;

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team'];
    this.accountId = accountId;
    this.db = null;
  }

  async initDb() {
    if (this.db) return this.db;
    const dbName = `cw-store-${this.accountId}`;

    let timeoutId;
    const openTimeout = new Promise((resolve, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error('IndexedDB open timed out')),
        DB_OPEN_TIMEOUT_MS
      );
    });

    try {
      this.db = await Promise.race([
        openDB(dbName, DATA_VERSION, {
          upgrade(db) {
            db.createObjectStore('cache-keys');
            db.createObjectStore('inbox', { keyPath: 'id' });
            db.createObjectStore('label', { keyPath: 'id' });
            db.createObjectStore('team', { keyPath: 'id' });
          },
        }),
        openTimeout,
      ]);
    } finally {
      clearTimeout(timeoutId);
    }

    // Store the database name in LocalStorage
    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    if (!dbNames.includes(dbName)) {
      dbNames.push(dbName);
      localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
    }

    return this.db;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    Object.keys(cacheKeys).forEach(async modelName => {
      this.db.put('cache-keys', cacheKeys[modelName], modelName);
    });
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
